import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  AllPlatformsForTemplateBuilderQuery,
  GetStructureDetailsDocument,
  ImageOrientation,
  ReportConfig,
  TemplateTypeEnum,
  useAddReportConfigurationMutation,
  useAddStructurePipesMutation,
  useDeleteStructurePipeMutation,
  useAllPlatformsForTemplateBuilderQuery,
  useAllPointOfInterestTemplateQuery,
  useAllTemplatesForBuilderQuery,
  useUpdateStructureMutation,
  useEditStructurePipeMutation,
  useUpdateFleetHealthMutation,
  ReportOperationalFields,
} from '@/__generated__/graphql';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { ReportConfigList } from './ReportConfigList';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { ImageUploadDialog } from './ImageUploadDialog';
import { FleetHealthTemplateModal } from './FleetHealthTemplateModal';
import { FALLBACK_INSPECTION_IMAGE, POI_REPORT_OPERATIONAL_TABLE } from '@/constants';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import Warning from '@mui/icons-material/Warning';
import Edit from '@mui/icons-material/Edit';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import Add from '@mui/icons-material/Add';
import CheckCircle from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import Chip from '@mui/material/Chip';
import Delete from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import { NominationFieldsEditor } from './NominationFieldsEditor';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { allPOITypes, selectedStructure, selectedTemplateId } from '../state';
import { StructureImg } from '../styles';
import { orderReportConfig, removeTypename, useSnackBarMessage } from '../utils';
import {
  formatEnumForDisplay,
  mergePoiOperationalTableFields,
  titleCaseFromKey,
  upperSnakeCase,
} from '@/utils';
import {
  DropDownContainer,
  dropDownStyles,
  Field,
  FieldPanel,
  FieldsList,
  FormHeader,
  LineBreak,
  tooltipStyles,
} from './styles';
import {
  Features,
  FeatureSection,
  FeatureValueMap,
  InsightsFeature,
  ViewerFeature,
} from '../types';
import {
  INSIGHTS_OPTIONS,
  POI_REPORT_OPERATIONAL_TABLE_KEYS,
  REPORT_CONFIG_KEYS,
  StructureSection,
  VIEWER_OPTIONS,
  POI_SCHEMA_FIELDS,
  PoiFieldOption,
} from '../constants';
import { ALLOWED_NOMINATION_POI_TEMPLATE } from '@/constants';
import { DropDownField } from '@/components/shared/CustomField';

import {
  ConfigurationTable,
  ColumnDefinition,
} from '@/components/TemplateBuilder/RiskConfiguration/ConfigurationTable';
import { CofConfigItem, POIWidgets } from '@/types';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';

type ImageOrientationConfigState = {
  poiReportPoiImages?: ImageOrientation | null;
  poiReportDeckImages?: ImageOrientation | null;
  componentReportPoiImages?: ImageOrientation | null;
  componentReportDeckImages?: ImageOrientation | null;
};

const getOrientationValue = (
  config: ImageOrientationConfigState | null | undefined,
  primaryKey: keyof ImageOrientationConfigState,
  fallbackKeys: (keyof ImageOrientationConfigState)[] = []
): ImageOrientation => {
  if (!config) return ImageOrientation.Default;

  const keys: (keyof ImageOrientationConfigState)[] = [primaryKey, ...fallbackKeys];
  for (const key of keys) {
    const value = config[key];
    if (value) {
      return value;
    }
  }

  return ImageOrientation.Default;
};

type StructureTemplateFormProps = {
  structureId: string;
  structurePipesData?: { key: string; value: number };
};

type NominationTableColumn = {
  key?: string | null;
  displayName?: string | null;
};

type NominationTableConfig = {
  title?: string | null;
  columns?: Array<NominationTableColumn | null>;
};

type NominationConfig = {
  poiTemplateNominationTypes?: Array<string | null>;
  nominationTableConfig?: NominationTableConfig;
};

type Structure = {
  name: string;
  scanDate: string | null;
  isActive: boolean;
  isWallLossEnabled: boolean;
  platform: string | null;
  hasRiskAssessment: boolean;
  category: string | null;
  fleetHealthTemplateField: string | null;
  features: Features;
  reportConfig: ReportConfig;
  nominationConfig?: NominationConfig;
};

export const StructureTemplateForm = ({
  structureId,
  structurePipesData,
}: StructureTemplateFormProps) => {
  let mid = Math.ceil(REPORT_CONFIG_KEYS.length / 2);
  const reportCongfigColumns = [REPORT_CONFIG_KEYS.slice(0, mid), REPORT_CONFIG_KEYS.slice(mid)];
  mid = Math.ceil(POI_REPORT_OPERATIONAL_TABLE_KEYS.length / 2);
  const poiReportOperationalFields = [
    POI_REPORT_OPERATIONAL_TABLE_KEYS.slice(0, mid),
    POI_REPORT_OPERATIONAL_TABLE_KEYS.slice(mid),
  ];
  // componentInspectionTableColumns is now generated dynamically from reportConfig fields
  const [structurePipes, setStructurePipes] = useState<CofConfigItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addPipesToStructure] = useAddStructurePipesMutation();
  const [deleteStructurePipe] = useDeleteStructurePipeMutation();
  const [editStructurePipe] = useEditStructurePipeMutation({
    refetchQueries: [GetStructureDetailsDocument],
  });

  const mapStructurePipes = useMemo(() => {
    if (!structurePipesData) return;
    return Object.entries(structurePipesData).map(([key, value]) => ({
      id: key,
      fieldKey: key,
      score: Number(value),
    }));
  }, [structurePipesData]);

  useEffect(() => {
    setStructurePipes((prev) => {
      if (!prev || prev.length === 0) {
        return mapStructurePipes ?? [];
      }
      return prev;
    });
  }, [mapStructurePipes]);

  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const structure = useAtomValue(selectedStructure);
  const [localFields, setLocalFields] = useState<Structure | null>(null);
  const [allPlatforms, setAllPlatforms] = useState<
    AllPlatformsForTemplateBuilderQuery['allPlatforms']
  >([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isFleetHealthModalOpen, setIsFleetHealthModalOpen] = useState(false);
  const pendingFleetHealthFieldRef = useRef<string | null | undefined>(undefined); // undefined = not pending
  const saveInProgressRef = useRef(false);
  const [selectedField, setSelectedField] = useState<StructureSection>(
    StructureSection.StructureDetails
  );
  const [allPointOfInterestTemplates, setAllPointOfInterestTemplates] = useAtom(allPOITypes);
  const selectedAssemblyTemplateId = useAtomValue(selectedTemplateId);
  const [assemblyTemplateFields, setAssemblyTemplateFields] = useState<
    Array<{
      name: string;
      key: string;
      options: string[] | null;
      optionsData: Array<{
        value: string | null;
        weight?: number | null;
        min?: number | null;
        max?: number | null;
        minIncluded?: boolean | null;
        maxIncluded?: boolean | null;
      }> | null;
    }>
  >([]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  // Filter fields: only show ones with more than 1 option
  const filteredAssemblyFields = useMemo(() => {
    return assemblyTemplateFields.filter((field) => {
      // Check if has more than 1 option
      const optionsCount = field.options?.length ?? field.optionsData?.length ?? 0;
      return optionsCount;
    });
  }, [assemblyTemplateFields]);

  // Compute preview data from current structure's fleetHealthTemplateField
  const fleetHealthPreview = useMemo(() => {
    const fieldKey = (structure as { fleetHealthTemplateField?: string | null })
      ?.fleetHealthTemplateField;

    if (!fieldKey) {
      return {
        fieldName: null,
        options: [] as string[],
        totalCount: 0,
        hasOptions: false,
      };
    }

    const field = assemblyTemplateFields.find((f) => f.key === fieldKey);

    if (!field) {
      return {
        fieldName: null,
        options: [] as string[],
        totalCount: 0,
        hasOptions: false,
      };
    }

    let previewOptions: string[] = [];
    if (field.options && field.options.length > 0) {
      previewOptions = field.options;
    } else if (field.optionsData && field.optionsData.length > 0) {
      previewOptions = field.optionsData
        .map((opt) => opt.value)
        .filter((val): val is string => val !== null && val !== undefined);
    }

    return {
      fieldName: field.name,
      options: previewOptions.slice(0, 10),
      totalCount: previewOptions.length,
      hasOptions: previewOptions.length > 0,
    };
  }, [structure, assemblyTemplateFields]);

  // Show modal when structure data has refreshed with the new fleet health field value
  useEffect(() => {
    if (pendingFleetHealthFieldRef.current === undefined) return; // Not pending

    const currentStructureField =
      (structure as { fleetHealthTemplateField?: string | null })?.fleetHealthTemplateField ?? null;

    // Check if structure has been updated with the expected value
    if (currentStructureField === pendingFleetHealthFieldRef.current) {
      setIsFleetHealthModalOpen(true);
      pendingFleetHealthFieldRef.current = undefined; // Reset
    }
  }, [structure]);

  const { data: platformsData } = useAllPlatformsForTemplateBuilderQuery({
    fetchPolicy: 'cache-first',
  });

  const { data: poiTemplatesData } = useAllPointOfInterestTemplateQuery({
    variables: {
      structureId,
      assemblyTemplateId: selectedAssemblyTemplateId || '',
    },
    skip: !structureId || !selectedAssemblyTemplateId,
    fetchPolicy: 'no-cache',
  });

  const { data: assemblyTemplatesData } = useAllTemplatesForBuilderQuery({
    variables: {
      type: TemplateTypeEnum.Assembly,
      structureId,
    },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (platformsData?.allPlatforms) {
      setAllPlatforms(platformsData.allPlatforms);
    }
  }, [platformsData?.allPlatforms]);

  useEffect(() => {
    if (poiTemplatesData?.allPointOfInterestTemplate) {
      setAllPointOfInterestTemplates(poiTemplatesData?.allPointOfInterestTemplate ?? []);
    }
  }, [poiTemplatesData?.allPointOfInterestTemplate, setAllPointOfInterestTemplates]);

  useEffect(() => {
    if (assemblyTemplatesData?.allTemplates && assemblyTemplatesData.allTemplates.length > 0) {
      // Get all fields from all assembly templates
      const allFields = assemblyTemplatesData.allTemplates
        .flatMap((template) => template.fields ?? [])
        .filter((field) => field.key !== null && field.key !== undefined)
        .map((field) => ({
          name: field.name,
          key: field.key as string,
          options: field.options ?? null,
          optionsData: field.optionsData ?? null,
        }));

      // Remove duplicates based on key
      const uniqueFields = Array.from(
        new Map(allFields.map((field) => [field.key, field])).values()
      );
      setAssemblyTemplateFields(uniqueFields);
    }
  }, [assemblyTemplatesData?.allTemplates]);

  const [updateStructureDetails, { loading: isLoading }] = useUpdateStructureMutation({
    refetchQueries: [GetStructureDetailsDocument],
  });

  const [updateFleetHealth] = useUpdateFleetHealthMutation();

  const defaultPoiSummaryChartsConfig = useMemo(() => {
    const templates = allPointOfInterestTemplates ?? [];
    const commonWidgets: POIWidgets[] = [POIWidgets.Status, POIWidgets.DueDate];
    const templateWidgets = new Set<POIWidgets>([
      POIWidgets.InspectionPriority,
      POIWidgets.PaintDamageAlarmLevels,
    ]);
    const poiWidgetOrder = Object.values(POIWidgets);

    const widgetsForFields = (
      fields: Array<{ name?: string | null } | null> | null | undefined
    ) => {
      const visibleTemplateWidgets = (fields ?? [])
        .map((f) => f?.name)
        .filter((name): name is POIWidgets => !!name && templateWidgets.has(name as POIWidgets));

      const allWidgets = Array.from(new Set([...commonWidgets, ...visibleTemplateWidgets]));
      return allWidgets.sort((a, b) => poiWidgetOrder.indexOf(a) - poiWidgetOrder.indexOf(b));
    };

    const config: Record<string, POIWidgets[]> = {};

    // "ALL" tab currently derives its fields from the first POI template (see Insights container),
    // so we mirror that behavior for default config.
    config.ALL = widgetsForFields(
      templates[0]?.fields as unknown as Array<{ name?: string | null } | null>
    );

    templates.forEach((template) => {
      const key = upperSnakeCase((template?.key ?? template?.name ?? '').toString());
      if (!key) return;
      config[key] = widgetsForFields(
        template?.fields as unknown as Array<{ name?: string | null } | null>
      );
    });

    return config;
  }, [allPointOfInterestTemplates]);

  useEffect(() => {
    if (!structure) return;
    if (mode !== 'ReadOnly') return; // Only reset localFields when not editing

    const fieldsOnly = Object.fromEntries(
      Object.entries(structure).filter(
        ([key]) => !['id', 'name', 'coverImagePath', '__typename'].includes(key)
      )
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { navigation, ...structureFeatures } = structure.features;

    const sanitizedReportConfig = structure.reportConfig
      ? {
          id: structure.reportConfig.id,
          title: structure.reportConfig.title,
          configurations:
            structure.reportConfig.configurations?.map(({ __typename, ...rest }) => rest) ?? [],
          poiReportOperationalTable: {
            fields: mergePoiOperationalTableFields(
              structure?.reportConfig?.poiReportOperationalTable
                ?.fields as ReportOperationalFields[],
              POI_REPORT_OPERATIONAL_TABLE.fields
            ).map((field) => {
              if (!field) return field;
              // Remove __typename if it exists (added by Apollo at runtime)
              const fieldCopy = { ...field };
              delete (fieldCopy as { __typename?: string }).__typename;
              return fieldCopy;
            }),

            title:
              structure.reportConfig.poiReportOperationalTable?.title ??
              POI_REPORT_OPERATIONAL_TABLE.title,
          },
          paintBlockReportPoiTemplateTypes:
            structure.reportConfig.paintBlockReportPoiTemplateTypes ?? [],
          reviewActionTableConfigs: (() => {
            const configs = structure.reportConfig.reviewActionTableConfigs;
            if (!configs) return {};
            // Handle both JSON string and object
            if (typeof configs === 'string') {
              try {
                return JSON.parse(configs) as Record<
                  string,
                  { fields?: ReportOperationalFields[] }
                >;
              } catch {
                return {};
              }
            }
            return (configs as Record<string, { fields?: ReportOperationalFields[] }>) ?? {};
          })(),
          poiSummaryChartsConfig: (() => {
            const cfg = (structure.reportConfig as unknown as { poiSummaryChartsConfig?: unknown })
              ?.poiSummaryChartsConfig;
            if (!cfg) return undefined;
            if (typeof cfg === 'string') {
              try {
                return JSON.parse(cfg) as Record<string, POIWidgets[]>;
              } catch {
                return undefined;
              }
            }
            return cfg as Record<string, POIWidgets[]>;
          })(),
          imageOrientationConfig: {
            ...(structure.reportConfig.imageOrientationConfig
              ? removeTypename(structure.reportConfig.imageOrientationConfig)
              : {}),
          },
        }
      : {
          id: '',
          title: '',
          configurations: [],
          paintBlockReportPoiTemplateTypes: [],
          poiReportOperationalTable: {
            fields: POI_REPORT_OPERATIONAL_TABLE.fields as ReportOperationalFields[],
            title: POI_REPORT_OPERATIONAL_TABLE.title,
          },
          reviewActionTableConfigs: {},
          poiSummaryChartsConfig: undefined,
          imageOrientationConfig: {},
        };

    setLocalFields({
      ...fieldsOnly,
      name: structure.name ?? '',
      scanDate: structure.scanDate ?? '',
      isActive: structure.isActive ?? false,
      platform: structure.platform?.name ?? '',
      isWallLossEnabled: structure.isWallLossEnabled ?? false,
      hasRiskAssessment: structure.hasRiskAssessment ?? false,
      category: structure.category ?? null,
      fleetHealthTemplateField:
        (structure as { fleetHealthTemplateField?: string | null }).fleetHealthTemplateField ??
        null,
      features: structureFeatures,
      reportConfig: sanitizedReportConfig,
      nominationConfig: (() => {
        const config = removeTypename(structure.nominationConfig) ?? {};
        const baseTypes = (config.poiTemplateNominationTypes ?? []) as string[];
        const defaults = ['PIT', 'BLISTER'];
        const mergedtypes = Array.from(
          new Set([...baseTypes.map((type) => (type ? type.toUpperCase() : type)), ...defaults])
        );
        const normalized = {
          poiTemplateNominationTypes: mergedtypes,
          nominationTableConfig: {
            title: config.nominationTableConfig?.title ?? 'Allowed POIs for Nomination',
            columns: config.nominationTableConfig?.columns ?? [],
          },
        };
        return normalized;
      })(),
    });
  }, [structure, mode]);

  useEffect(() => {
    if (!localFields) return;
    if (!localFields.reportConfig) return;
    if (!allPointOfInterestTemplates || allPointOfInterestTemplates.length === 0) return;
    const current = (localFields.reportConfig as unknown as { poiSummaryChartsConfig?: unknown })
      ?.poiSummaryChartsConfig;
    if (
      current &&
      typeof current === 'object' &&
      Object.keys(current as Record<string, unknown>).length > 0
    ) {
      return;
    }
    setLocalFields((prev) => {
      if (!prev?.reportConfig) return prev;
      const existing = (prev.reportConfig as unknown as { poiSummaryChartsConfig?: unknown })
        ?.poiSummaryChartsConfig;
      if (
        existing &&
        typeof existing === 'object' &&
        Object.keys(existing as Record<string, unknown>).length > 0
      ) {
        return prev;
      }
      return {
        ...prev,
        reportConfig: {
          ...prev.reportConfig,
          poiSummaryChartsConfig: defaultPoiSummaryChartsConfig,
        } as unknown as ReportConfig,
      };
    });
  }, [allPointOfInterestTemplates, defaultPoiSummaryChartsConfig, localFields]);

  const handleEditStart = (item: CofConfigItem) => {
    setEditingId(item.id);
  };

  const handleEditCancel = () => {
    setEditingId(null);
  };
  const handleInlineChange = (
    id: string,
    field: keyof Omit<CofConfigItem, 'id'>,
    value: string | number
  ) => {
    setStructurePipes((prev) =>
      prev.map((pipe) =>
        pipe.id === id ? { ...pipe, [field]: field === 'score' ? Number(value) : value } : pipe
      )
    );
  };

  const performSave = useCallback(() => {
    if (!localFields) return;
    if (saveInProgressRef.current) return; // Prevent duplicate saves

    saveInProgressRef.current = true;

    // Sanitize reportConfig: remove id and __typename from fields
    const sanitizedReportConfig = localFields.reportConfig
      ? {
          ...localFields.reportConfig,
          reviewActionTableConfigs: localFields.reportConfig.reviewActionTableConfigs
            ? Object.entries(
                localFields.reportConfig.reviewActionTableConfigs as Record<
                  string,
                  { fields?: ReportOperationalFields[] }
                >
              ).reduce(
                (acc, [key, config]) => {
                  acc[key] = {
                    fields: (config.fields || []).map((field) => {
                      if (!field) return field;
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      const { __typename, id, ...sanitizedField } = field;
                      return sanitizedField;
                    }),
                  };
                  return acc;
                },
                {} as Record<string, { fields: ReportOperationalFields[] }>
              )
            : undefined,
        }
      : undefined;

    const nominationConfig = localFields.nominationConfig ?? {
      poiTemplateNominationTypes: [],
      nominationTableConfig: { title: 'Allowed POIs for Nomination', columns: [] },
    };

    const nominationConfigToSend = {
      poiTemplateNominationTypes: nominationConfig.poiTemplateNominationTypes ?? [],
      nominationTableConfig:
        nominationConfig.nominationTableConfig === null
          ? undefined
          : {
              title: nominationConfig.nominationTableConfig?.title ?? null,
              columns: (nominationConfig.nominationTableConfig?.columns ?? []).map((f) => ({
                key: f?.key ?? null,
                displayName: f?.displayName ?? null,
              })),
            },
    };

    updateStructureDetails({
      variables: {
        input: {
          structureId,
          name: localFields.name,
          scanDate: localFields.scanDate,
          isActive: localFields.isActive,
          isWallLossEnabled: localFields.isWallLossEnabled,
          platformId: allPlatforms.find(
            (platform) => platform.name.toLowerCase() === localFields.platform?.toLowerCase()
          )?.id,
          hasRiskAssessment: localFields.hasRiskAssessment,
          category: localFields.category || null,
          fleetHealthTemplateField: localFields.fleetHealthTemplateField || null,
          features: localFields.features,
          reportConfig: sanitizedReportConfig,
          nominationConfig: nominationConfigToSend,
          lastModifiedFor: 'Update Structure Details',
        },
      },
      onCompleted: (response) => {
        if (response.updateStructure && saveInProgressRef.current) {
          showSnackBar('Structure updated successfully', 'success');
          setMode('ReadOnly');
        }
        saveInProgressRef.current = false;
      },
      onError: (error) => {
        console.error(error);
        showSnackBar(`Failed to update structure - ${error.message}`, 'error');
        pendingFleetHealthFieldRef.current = undefined; // Reset on error
        saveInProgressRef.current = false;
      },
    });
  }, [allPlatforms, localFields, showSnackBar, structureId, updateStructureDetails]);

  const handleSaveStructure = useCallback(() => {
    if (!localFields) return;

    // Check if fleet health template field has changed
    const originalValue =
      (structure as { fleetHealthTemplateField?: string | null }).fleetHealthTemplateField ?? null;
    const hasFleetHealthChanged = localFields.fleetHealthTemplateField !== originalValue;

    // Store the expected field value - modal will open via useEffect when structure updates
    if (hasFleetHealthChanged) {
      pendingFleetHealthFieldRef.current = localFields.fleetHealthTemplateField ?? null;
    }

    // Perform save
    performSave();
  }, [localFields, structure, performSave]);

  const [addReportConfigurationMutation] = useAddReportConfigurationMutation({
    refetchQueries: [GetStructureDetailsDocument],
  });

  const handleAddReportConfig = useCallback(() => {
    addReportConfigurationMutation({
      variables: {
        structureId: structureId,
      },
      onCompleted: (response) => {
        if (!response?.addReportConfiguration) {
          showSnackBar('An error occurred while adding report configuration', 'error');
          setIsConfirmationDialogOpen(false);
          return;
        }
        showSnackBar('Report configuration added successfully', 'success');
        setIsConfirmationDialogOpen(false);
      },
      onError: (error) => {
        console.error(error);
        showSnackBar(`Failed to add report configuration - ${error.message}`, 'error');
        setIsConfirmationDialogOpen(false);
      },
    });
  }, [addReportConfigurationMutation, showSnackBar, structureId]);

  const handleAddStructurePipe = useCallback(
    async (newItem: Omit<CofConfigItem, 'id'>) => {
      const parsed = { ...newItem, score: newItem.score };

      const exists = (structurePipes ?? []).some((p) => p.fieldKey === parsed.fieldKey);
      if (exists) {
        showSnackBar(`Pipe key "${parsed.fieldKey}" already exists`, 'error');
        return;
      }

      try {
        const result = await addPipesToStructure({
          variables: {
            input: {
              structureId,
              imperialSteelPipes: { [parsed.fieldKey]: parsed.score },
            },
          },
        });

        if (!result || result.errors?.length) {
          throw new Error(result.errors?.[0]?.message ?? 'Error adding pipe');
        }

        setStructurePipes((prev) => [
          ...(prev ?? []),
          {
            id: parsed.fieldKey,
            fieldKey: parsed.fieldKey,
            score: parsed.score as number,
          },
        ]);

        showSnackBar(`Added pipe: ${parsed.fieldKey} — ${parsed.score}`, 'success');
      } catch (error) {
        console.error('Failed to add pipe:', error);
      }
    },
    [addPipesToStructure, structurePipes, showSnackBar, structureId]
  );

  const handleDeleteStructurePipe = useCallback(
    async (item: CofConfigItem) => {
      try {
        const res = await deleteStructurePipe({
          variables: {
            structureId,
            pipeSize: item.fieldKey,
          },
        });

        if (res.errors && res.errors.length > 0) {
          showSnackBar(res.errors[0].message, 'error');
          return;
        }
        setStructurePipes((prev) => (prev ?? []).filter((p) => p.fieldKey !== item.fieldKey));

        showSnackBar(`Removed pipe ${item.fieldKey}`, 'success');
      } catch (error) {
        console.error('Failed to delete pipe:', error);
      }
    },
    [deleteStructurePipe, structureId, showSnackBar]
  );

  const handleEditStructurePipe = useCallback(
    async (id: string, field: keyof Omit<CofConfigItem, 'id'>, value: string | number) => {
      setStructurePipes((prev) =>
        (prev ?? []).map((item) =>
          item.id === id
            ? {
                ...item,
                [field]: field === 'score' ? parseFloat(value as string) : value,
              }
            : item
        )
      );

      if (field !== 'score') return;

      const current = structurePipes ?? [];
      const target = current.find((p) => p.id === id);
      const pipeSize = target?.fieldKey;
      const pipeValue = typeof value === 'number' ? value : parseFloat(value as string);

      if (!pipeSize || Number.isNaN(pipeValue)) {
        showSnackBar('Invalid pipe value or missing pipe key', 'error');
        return;
      }

      try {
        const res = await editStructurePipe({
          variables: {
            structureId,
            pipeSize,
            pipeValue,
          },
        });

        if (res.errors && res.errors.length > 0) {
          showSnackBar(res.errors[0].message, 'error');
          return;
        }
        setEditingId(null);
        showSnackBar(`Updated pipe ${pipeSize} — ${pipeValue}`, 'success');
      } catch (error) {
        console.error('Failed to edit pipe:', error);
        showSnackBar('Failed to update pipe', 'error');
      }
    },
    [editStructurePipe, structureId, showSnackBar, structurePipes]
  );

  const handleCancel = useCallback(() => {
    if (structure && structure.reportConfig) {
      const sanitizedReportConfig = {
        id: structure.reportConfig.id,
        title: structure.reportConfig.title,
        configurations:
          structure.reportConfig.configurations?.map(({ __typename, ...rest }) => rest) ?? [],
        poiReportOperationalTable: {
          fields: (
            structure.reportConfig.poiReportOperationalTable?.fields ??
            POI_REPORT_OPERATIONAL_TABLE.fields
          ).map((field) => {
            if (!field) return field;
            const fieldCopy = { ...field };
            delete (fieldCopy as { __typename?: string }).__typename;
            return fieldCopy;
          }),
          title:
            structure.reportConfig.poiReportOperationalTable?.title ??
            POI_REPORT_OPERATIONAL_TABLE.title,
        },
        paintBlockReportPoiTemplateTypes:
          structure.reportConfig?.paintBlockReportPoiTemplateTypes ?? [],
        reviewActionTableConfigs: (() => {
          const configs = structure.reportConfig?.reviewActionTableConfigs;
          if (!configs) return {};
          // Handle both JSON string and object
          if (typeof configs === 'string') {
            try {
              return JSON.parse(configs) as Record<string, { fields?: ReportOperationalFields[] }>;
            } catch {
              return {};
            }
          }
          return (configs as Record<string, { fields?: ReportOperationalFields[] }>) ?? {};
        })(),
        imageOrientationConfig: {
          ...(structure.reportConfig.imageOrientationConfig
            ? removeTypename(structure.reportConfig.imageOrientationConfig)
            : {}),
        },
      };

      setLocalFields({
        ...structure,
        name: structure.name ?? '',
        scanDate: structure.scanDate ?? null,
        isActive: structure.isActive ?? false,
        platform: structure.platform?.name ?? null,
        isWallLossEnabled: structure.isWallLossEnabled ?? false,
        hasRiskAssessment: structure.hasRiskAssessment ?? false,
        category: structure.category ?? null,
        fleetHealthTemplateField:
          (structure as { fleetHealthTemplateField?: string | null }).fleetHealthTemplateField ??
          null,
        features: structure.features,
        reportConfig: sanitizedReportConfig,
        nominationConfig: (() => {
          const config = structure.nominationConfig ?? {};
          return {
            ...(config || {}),
            poiTemplateNominationTypes:
              structure.nominationConfig?.poiTemplateNominationTypes ?? [],
            nominationTableConfig: {
              title: config?.nominationTableConfig?.title ?? 'Allowed POIs for Nomination',
              columns: config?.nominationTableConfig?.columns ?? [],
            },
          };
        })(),
      });
    }
    setMode('ReadOnly');
  }, [structure]);

  const handleDialogOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleFleetHealthConfirm = useCallback(() => {
    // Get platform ID from structure
    const platformId = structure?.platform?.id;

    if (platformId) {
      // Call UpdateFleetHealth mutation
      updateFleetHealth({
        variables: {
          platformIds: [platformId],
        },
        onCompleted: () => {
          showSnackBar('Fleet health score generation started', 'success');
        },
        onError: (error) => {
          console.error('Error updating fleet health:', error);
          showSnackBar(`Failed to generate fleet health score - ${error.message}`, 'error');
        },
      });
    }

    // Close modal (save already completed)
    setIsFleetHealthModalOpen(false);
  }, [structure, updateFleetHealth, showSnackBar]);

  const handleFleetHealthCancel = useCallback(() => {
    // Close modal without generating score (save already completed)
    setIsFleetHealthModalOpen(false);
  }, []);

  const handleDialogClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleCheckboxChange = useCallback((section: FeatureSection, item: string = '') => {
    setLocalFields((prev) => {
      if (!prev) return prev;

      const updatedFeatures: Features = { ...prev.features };

      if (!item) {
        if (section in updatedFeatures) {
          delete updatedFeatures[section];
        } else {
          updatedFeatures[section] = [];
        }
      }

      return {
        ...prev,
        features: updatedFeatures,
      };
    });
  }, []);

  const handleNestedCheckboxChange = useCallback(
    <T extends keyof FeatureValueMap>(section: T, item: FeatureValueMap[T]) => {
      setLocalFields((prev) => {
        if (!prev) return prev;

        const updatedFeatures: Features = { ...prev.features };
        const existing = (updatedFeatures[section] ?? []) as FeatureValueMap[T][];
        const includes = existing.includes(item);

        const newItems = includes ? existing.filter((i) => i !== item) : [...existing, item];

        if (newItems.length === 0) {
          delete updatedFeatures[section];
        } else {
          updatedFeatures[section] = newItems as
            | (ViewerFeature[] & InsightsFeature[] & string[])
            | undefined;
        }

        return {
          ...prev,
          features: updatedFeatures,
        };
      });
    },
    []
  );

  const handleToggleKey = useCallback((key: string, checked: boolean) => {
    setLocalFields((prev) => {
      if (!prev || !prev.reportConfig) return prev;

      const current =
        prev.reportConfig.configurations?.map(({ __typename: _t, ...rest }) => rest) ?? [];

      const exists = current?.some((c) => c.key === key);
      let nextConfigurations;

      if (checked) {
        nextConfigurations = exists
          ? current
          : [...current, { key, title: titleCaseFromKey(key), id: '' }];
      } else {
        nextConfigurations = current?.filter((c) => c.key !== key) ?? [];
      }

      const nextReportConfig = {
        ...prev.reportConfig,
        configurations: orderReportConfig(nextConfigurations),
      };

      return { ...prev, reportConfig: nextReportConfig };
    });
  }, []);

  const handlePoiReportKeysToggle = useCallback((key: string, checked: boolean) => {
    setLocalFields((prev) => {
      if (!prev?.reportConfig) return prev;

      const current =
        (prev.reportConfig.poiReportOperationalTable?.fields as ReportOperationalFields[]) ?? [];

      const nextFields = current.map(({ __typename, ...rest }) => {
        if (rest.key === key) {
          return { ...rest, visibility: checked };
        }
        return rest;
      });

      return {
        ...prev,
        reportConfig: {
          ...prev.reportConfig,
          poiReportOperationalTable: {
            ...prev.reportConfig.poiReportOperationalTable,
            fields: nextFields,
          },
        },
      };
    });
  }, []);

  // Extract review actions from assembly templates
  const reviewActions = useMemo(() => {
    if (assemblyTemplatesData?.allTemplates && assemblyTemplatesData.allTemplates.length > 0) {
      const allReviewActions = assemblyTemplatesData.allTemplates
        .flatMap((template) => template.reviewActionChecklistConfig ?? [])
        .filter((config) => config.isEnabled)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

      if (allReviewActions.length > 0) {
        return allReviewActions;
      }
    }

    // Return empty array if no configuration found
    return [];
  }, [assemblyTemplatesData?.allTemplates]);

  // Helper function to get all fields (including already added ones) for a specific review action.
  const getAvailableComponentInspectionFields = useCallback(
    (reviewActionValue: string): PoiFieldOption[] => {
      void reviewActionValue; // Reserved for future filtering by selected keys
      // Include all schema fields (both selected and unselected) for display
      const schemaFields = POI_SCHEMA_FIELDS.map((field) => field);

      // Include all template fields (both selected and unselected) for display
      const templateFields: PoiFieldOption[] = [];
      if (allPointOfInterestTemplates) {
        allPointOfInterestTemplates.forEach((template) => {
          template.fields?.forEach((field) => {
            if (
              field.key &&
              field.displayInReports !== false &&
              !templateFields.some((tf) => tf.key === field.key)
            ) {
              templateFields.push({
                key: field.key,
                label: field.name || field.key,
                source: 'template',
              });
            }
          });
        });
      }

      // Return all fields (including already selected ones) so they can be shown with indicators
      return [...schemaFields, ...templateFields];
    },
    [allPointOfInterestTemplates]
  );

  const [selectedFieldForInspection, setSelectedFieldForInspection] = useState<
    Record<string, PoiFieldOption | null>
  >({});

  const handleAddComponentInspectionField = useCallback(
    (reviewActionValue: string) => {
      const selectedField = selectedFieldForInspection[reviewActionValue];
      if (!selectedField) return;

      setLocalFields((prev) => {
        if (!prev?.reportConfig) return prev;

        const reviewActionConfigs =
          (prev.reportConfig.reviewActionTableConfigs as
            | Record<string, { fields?: ReportOperationalFields[] }>
            | undefined) ?? {};
        const current = reviewActionConfigs[reviewActionValue]?.fields ?? [];

        if (current.some((f) => f.key === selectedField.key)) {
          return prev;
        }
        const visibleCount = current.filter((f) => f.visibility !== false).length;
        const newField: ReportOperationalFields = {
          key: selectedField.key,
          title: selectedField.label,
          visibility: visibleCount < 5,
        };

        return {
          ...prev,
          reportConfig: {
            ...prev.reportConfig,
            reviewActionTableConfigs: {
              ...reviewActionConfigs,
              [reviewActionValue]: {
                fields: [...current, newField],
              },
            },
          },
        };
      });

      // Clear selection for this review action
      setSelectedFieldForInspection((prev) => ({
        ...prev,
        [reviewActionValue]: null,
      }));
    },
    [selectedFieldForInspection]
  );

  const handleComponentInspectionTableToggle = useCallback(
    (reviewActionValue: string, key: string, checked: boolean) => {
      setLocalFields((prev) => {
        if (!prev?.reportConfig) return prev;

        const reviewActionConfigs =
          (prev.reportConfig.reviewActionTableConfigs as
            | Record<string, { fields?: ReportOperationalFields[] }>
            | undefined) ?? {};
        const current = reviewActionConfigs[reviewActionValue]?.fields ?? [];

        // Count currently checked fields (excluding the one being toggled)
        const currentlyCheckedCount = current.filter(
          (field) => field.key !== key && field.visibility !== false
        ).length;

        // If trying to check a 6th field, prevent it
        if (checked && currentlyCheckedCount >= 5) {
          showSnackBar('Only 5 fields are allowed in the component inspection table', 'warning');
          return prev; // Don't update state
        }

        const nextFields = current.map(({ __typename, ...rest }) => {
          if (rest.key === key) {
            return { ...rest, visibility: checked };
          }
          return rest;
        });

        return {
          ...prev,
          reportConfig: {
            ...prev.reportConfig,
            reviewActionTableConfigs: {
              ...reviewActionConfigs,
              [reviewActionValue]: {
                fields: nextFields,
              },
            },
          },
        };
      });
    },
    [showSnackBar]
  );

  const handlePoiReportOperationalTableName = useCallback((newTitle: string) => {
    setLocalFields((prev) => {
      if (!prev?.reportConfig) return prev;

      const current =
        (prev.reportConfig.poiReportOperationalTable?.fields as ReportOperationalFields[]) ?? [];
      // fields without __typename

      const nextFields = current.map(({ __typename, ...rest }) => {
        return rest;
      });
      return {
        ...prev,
        reportConfig: {
          ...prev.reportConfig,
          poiReportOperationalTable: {
            fields: nextFields,
            title: newTitle,
          },
        },
      };
    });
  }, []);

  const handleTitleChange = useCallback((key: string, newTitle: string) => {
    setLocalFields((prev) => {
      if (!prev || !prev.reportConfig) return prev;

      const nextConfigurations =
        prev.reportConfig.configurations?.map(({ __typename, ...config }) =>
          config.key === key ? { ...config, title: newTitle } : config
        ) ?? [];

      const nextReportConfig = {
        ...prev.reportConfig,
        configurations: orderReportConfig(nextConfigurations),
      };

      return { ...prev, reportConfig: nextReportConfig };
    });
  }, []);

  const handleConfigTitleChange = useCallback((newTitle: string) => {
    setLocalFields((prev) => {
      if (!prev || !prev.reportConfig) return prev;

      const nextReportConfig = {
        ...prev.reportConfig,
        title: newTitle,
      };

      return { ...prev, reportConfig: nextReportConfig };
    });
  }, []);

  const columns = useMemo((): ColumnDefinition<CofConfigItem>[] => {
    return [
      {
        key: 'fieldKey',
        label: 'Imperial',
        width: 4,
        type: 'number',
        inputProps: { step: 0.01, min: 0 },
      },
      {
        key: 'score',
        label: 'Metric',
        width: 4,
        type: 'number',
        inputProps: { step: 0.01, min: 0 },
      },
    ];
  }, []);
  return (
    <Stack paddingTop={4}>
      <FormHeader>
        <Typography fontSize="2.2rem" fontWeight={500}>
          {structure?.name ?? 'Structure'}
        </Typography>

        {mode === 'ReadOnly' ? (
          <Tooltip
            title="Edit Structure"
            placement="top"
            arrow
            slotProps={{
              popper: { sx: tooltipStyles },
            }}
          >
            <IconButton
              onClick={() => setMode('Edit')}
              disabled={selectedField === StructureSection.ReportConfig && !structure?.reportConfig}
            >
              <Edit
                fontSize="inherit"
                sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleSaveStructure}
              disabled={isLoading}
            >
              {isLoading ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </FormHeader>

      <LineBreak marginBottom={2} />

      <Stack direction="row" width="100%" gap={2}>
        <FieldsList>
          <Stack gap={1}>
            <Field
              key={`${StructureSection.StructureDetails}-${structure?.id}`}
              selected={selectedField === StructureSection.StructureDetails}
              onClick={() => {
                setSelectedField(StructureSection.StructureDetails);
              }}
            >
              Structure Details
            </Field>
            <Field
              key={`${StructureSection.Features}-${structure?.id}`}
              selected={selectedField === StructureSection.Features}
              onClick={() => {
                setSelectedField(StructureSection.Features);
              }}
            >
              Features
            </Field>
            <Field
              key={`${StructureSection.ReportConfig}-${structure?.id}`}
              selected={selectedField === StructureSection.ReportConfig}
              onClick={() => {
                setSelectedField(StructureSection.ReportConfig);
              }}
            >
              Report Configuration
            </Field>

            <Field
              key={`${StructureSection.SummaryChartsConfig}-${structure?.id}`}
              selected={selectedField === StructureSection.SummaryChartsConfig}
              onClick={() => {
                setSelectedField(StructureSection.SummaryChartsConfig);
              }}
            >
              Summary charts config
            </Field>

            <Field
              key={`${StructureSection.NominationPoiTypes}-${structure?.id}`}
              selected={selectedField === StructureSection.NominationPoiTypes}
              onClick={() => {
                setSelectedField(StructureSection.NominationPoiTypes);
              }}
            >
              Point Of Interests Nomination
            </Field>
            <Field
              key={`${StructureSection.StructurePipes}-${structure?.id}`}
              selected={selectedField === StructureSection.StructurePipes}
              onClick={() => {
                setSelectedField(StructureSection.StructurePipes);
              }}
            >
              Structure Pipes
            </Field>
          </Stack>
        </FieldsList>

        {localFields ? (
          <>
            {selectedField === StructureSection.StructureDetails && localFields && (
              <FieldPanel gap={1} direction="row">
                <Stack width="50%" gap={1}>
                  <Grid container maxWidth="60rem" spacing={4}>
                    <Grid size={6}>
                      <TextField
                        label="Structure Name"
                        value={localFields.name}
                        onChange={(e) =>
                          setLocalFields({
                            ...localFields,
                            name: e.target.value,
                          })
                        }
                        onBlur={(e) =>
                          setLocalFields({
                            ...localFields,
                            name: e.target.value.trim(),
                          })
                        }
                        fullWidth
                        placeholder={localFields.name}
                        disabled={mode === 'ReadOnly'}
                        variant="standard"
                      />
                    </Grid>

                    <Grid size={6}>
                      <DropDownContainer>
                        <Autocomplete
                          fullWidth
                          disableCloseOnSelect
                          includeInputInList
                          autoHighlight
                          disabled={mode === 'ReadOnly'}
                          options={allPlatforms.map((platform) => platform.name)}
                          value={localFields.platform ?? ''}
                          onChange={(_event, value) =>
                            setLocalFields({ ...localFields, platform: value ?? '' })
                          }
                          noOptionsText="No platforms found"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              label="Select Platform"
                              placeholder="Select platform"
                              fullWidth
                            />
                          )}
                        />
                      </DropDownContainer>
                    </Grid>

                    <Grid size={6}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!localFields.isActive}
                              disabled={mode === 'ReadOnly'}
                              onChange={(event) =>
                                setLocalFields({
                                  ...localFields,
                                  isActive: event.target.checked,
                                })
                              }
                            />
                          }
                          label="Is Active Deployment?"
                        />
                      </FormGroup>
                    </Grid>

                    <Grid size={6}>
                      <TextField
                        label="Scan Date"
                        value={localFields.scanDate}
                        onChange={(e) =>
                          setLocalFields({
                            ...localFields,
                            scanDate: e.target.value,
                          })
                        }
                        onBlur={(e) =>
                          setLocalFields({
                            ...localFields,
                            scanDate: e.target.value.trim(),
                          })
                        }
                        fullWidth
                        placeholder="MMMM, yyyy"
                        disabled={mode === 'ReadOnly'}
                        variant="standard"
                      />
                    </Grid>
                    <Grid size={6}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!localFields.isWallLossEnabled}
                              disabled={mode === 'ReadOnly'}
                              onChange={(event) =>
                                setLocalFields({
                                  ...localFields,
                                  isWallLossEnabled: event.target.checked,
                                })
                              }
                            />
                          }
                          label="Is Wall Loss Enabled?"
                        />
                      </FormGroup>

                      <Typography fontSize="1.3rem" fontWeight={400}>
                        <Warning sx={{ fontSize: '1.4rem', color: abyssColors.warning[500] }} />
                        <span>
                          To update existing assembly calculations, go to the Assembly tab and click
                          MI Recalculation
                        </span>
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <FormGroup>
                        <Tooltip
                          title="Enable ES Sheet"
                          placement="bottom"
                          arrow
                          slotProps={{
                            popper: { sx: tooltipStyles },
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!localFields.hasRiskAssessment}
                                disabled={mode === 'ReadOnly'}
                                onChange={(event) =>
                                  setLocalFields({
                                    ...localFields,
                                    hasRiskAssessment: event.target.checked,
                                  })
                                }
                              />
                            }
                            label="Has Risk Assessment?"
                          />
                        </Tooltip>
                      </FormGroup>
                    </Grid>
                    <Grid size={6}>
                      <DropDownContainer>
                        <Autocomplete
                          freeSolo
                          fullWidth
                          disableClearable={false}
                          includeInputInList
                          autoHighlight
                          disabled={mode === 'ReadOnly'}
                          options={['Pressure', 'Structure', 'Coatings Manager']}
                          value={localFields.category || ''}
                          onChange={(_event, value) => {
                            // Handle both suggestion selection and custom text input
                            if (value === null || value === '') {
                              setLocalFields({
                                ...localFields,
                                category: null,
                              });
                              return;
                            }

                            // Use the value as-is (can be enum suggestion or custom text)
                            setLocalFields({
                              ...localFields,
                              category: value,
                            });
                          }}
                          onInputChange={(_event, newInputValue, reason) => {
                            // Handle custom text input when user types (not just when selecting)
                            // This ensures custom values are captured even if not in options
                            if (reason === 'input' && newInputValue) {
                              // Only update if it's not already set (to avoid conflicts with onChange)
                              if (localFields.category !== newInputValue) {
                                setLocalFields({
                                  ...localFields,
                                  category: newInputValue,
                                });
                              }
                            } else if (reason === 'clear') {
                              setLocalFields({
                                ...localFields,
                                category: null,
                              });
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              label="Deployment Category"
                              placeholder="Select or type category"
                              helperText="Select from suggestions (Pressure, Structure, Coatings Manager) or type a new category"
                              fullWidth
                            />
                          )}
                        />
                      </DropDownContainer>
                    </Grid>
                    <Grid size={6}>
                      <DropDownContainer>
                        <Autocomplete
                          fullWidth
                          disableClearable={false}
                          includeInputInList
                          autoHighlight
                          disabled={mode === 'ReadOnly'}
                          options={filteredAssemblyFields.map((field) => field.key)}
                          getOptionLabel={(option) => {
                            const field = filteredAssemblyFields.find((f) => f.key === option);
                            return field?.name || option;
                          }}
                          value={localFields.fleetHealthTemplateField || null}
                          onChange={(_event, value) => {
                            setLocalFields({
                              ...localFields,
                              fleetHealthTemplateField: value || null,
                            });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              label="Fleet Health Template"
                              placeholder="Select a field"
                              helperText="Select a field from the Assembly template (fields with multiple options)"
                              fullWidth
                            />
                          )}
                        />
                      </DropDownContainer>
                    </Grid>
                  </Grid>
                </Stack>

                <Stack
                  width="50%"
                  gap={1}
                  sx={{
                    background: abyssColors.primary[100],
                    padding: 4,
                    gap: '16px',
                    alignItems: 'center',
                  }}
                >
                  <Grid container maxWidth="60rem" spacing={4}>
                    <Grid size={12}>
                      <StructureImg>
                        <img
                          style={{ width: '100%' }}
                          src={
                            structure?.coverImagePath
                              ? getCloudfrontUrl(structure.coverImagePath)
                              : FALLBACK_INSPECTION_IMAGE
                          }
                          alt={structure?.name}
                          onError={(error) => (error.currentTarget.src = FALLBACK_INSPECTION_IMAGE)}
                          crossOrigin="use-credentials"
                        />
                      </StructureImg>
                    </Grid>

                    <Grid size={12}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={mode === 'ReadOnly'}
                        startIcon={<FileUploadOutlined />}
                        onClick={() => handleDialogOpen()}
                      >
                        Upload Platform Image
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </FieldPanel>
            )}

            {selectedField === StructureSection.Features && localFields.features && (
              <FieldPanel gap={1} direction="row">
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={12}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="h6">Insights</Typography>
                      <FormGroup>
                        {INSIGHTS_OPTIONS.map(({ label, key }) => (
                          <FormControlLabel
                            key={key}
                            control={
                              <Checkbox
                                checked={localFields.features.insights?.includes(key) || false}
                                onChange={() => handleNestedCheckboxChange('insights', key)}
                                disabled={mode === 'ReadOnly'}
                              />
                            }
                            label={label}
                          />
                        ))}
                      </FormGroup>

                      <Typography variant="h6" sx={{ mt: 3 }}>
                        Risk Assessment
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={'riskAssessment' in localFields.features}
                              onChange={() => handleCheckboxChange('riskAssessment', '')}
                              disabled={mode === 'ReadOnly'}
                            />
                          }
                          label="Risk Assessment"
                        />
                      </FormGroup>

                      <Typography variant="h6" sx={{ mt: 3 }}>
                        Reports
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={'reports' in localFields.features}
                              onChange={() => handleCheckboxChange('reports', '')}
                              disabled={mode === 'ReadOnly'}
                            />
                          }
                          label="Reports"
                        />
                      </FormGroup>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="h6">Viewer</Typography>
                      <FormGroup>
                        {VIEWER_OPTIONS.map(({ label, key }) => (
                          <FormControlLabel
                            key={key}
                            control={
                              <Checkbox
                                checked={localFields.features.viewer?.includes(key) || false}
                                onChange={() => handleNestedCheckboxChange('viewer', key)}
                                disabled={mode === 'ReadOnly'}
                              />
                            }
                            label={label}
                          />
                        ))}
                      </FormGroup>

                      <Typography variant="h6" sx={{ mt: 3 }}>
                        IDMS
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={'idms' in localFields.features}
                              onChange={() => handleCheckboxChange('idms', '')}
                              disabled={mode === 'ReadOnly'}
                            />
                          }
                          label="IDMS"
                        />
                      </FormGroup>

                      <Typography variant="h6" sx={{ mt: 3 }}>
                        Deck Plan
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={'deckPlan' in localFields.features}
                              onChange={() => handleCheckboxChange('deckPlan', '')}
                              disabled={mode === 'ReadOnly'}
                            />
                          }
                          label="Deck Plan"
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Box>
              </FieldPanel>
            )}

            {selectedField === StructureSection.ReportConfig && (
              <>
                {(localFields?.reportConfig?.configurations?.length ?? 0) > 0 ? (
                  <Stack spacing={2}>
                    <TextField
                      value={localFields?.reportConfig?.title ?? ''}
                      onChange={(e) => handleConfigTitleChange(e.target.value)}
                      disabled={mode !== 'Edit'}
                      label="Report Config Title"
                    />
                    <ReportConfigList
                      mode={mode}
                      reportConfig={localFields.reportConfig?.configurations ?? []}
                      columns={reportCongfigColumns}
                      onToggleKey={handleToggleKey}
                      onUpdateTitle={handleTitleChange}
                    />
                    <Box sx={{ paddingTop: '4rem' }}></Box>
                    <TextField
                      value={
                        localFields.reportConfig.poiReportOperationalTable?.title ??
                        POI_REPORT_OPERATIONAL_TABLE.title
                      }
                      onChange={(e) => handlePoiReportOperationalTableName(e.target.value)}
                      disabled={mode !== 'Edit'}
                      label="POI report operational fields"
                    />
                    <ReportConfigList
                      mode={mode}
                      reportConfig={
                        localFields.reportConfig.poiReportOperationalTable
                          ?.fields as ReportOperationalFields[]
                      }
                      columns={poiReportOperationalFields}
                      enableEditing={false}
                      onToggleKey={handlePoiReportKeysToggle}
                      onUpdateTitle={handleTitleChange}
                    />
                    <Box sx={{ paddingTop: '4rem' }}></Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Component Inspection Table Configuration
                    </Typography>
                    <Stack spacing={2}>
                      {reviewActions.map((reviewAction) => {
                        const reviewActionValue = reviewAction.reviewActionValue;
                        // Handle both object and JSON string
                        let reviewActionConfigs:
                          | Record<string, { fields?: ReportOperationalFields[] }>
                          | undefined;
                        const configs = localFields.reportConfig?.reviewActionTableConfigs;
                        if (typeof configs === 'string') {
                          try {
                            reviewActionConfigs = JSON.parse(configs);
                          } catch {
                            reviewActionConfigs = {};
                          }
                        } else {
                          reviewActionConfigs = configs as
                            | Record<string, { fields?: ReportOperationalFields[] }>
                            | undefined;
                        }
                        const inspectionFields =
                          reviewActionConfigs?.[reviewActionValue]?.fields ?? [];
                        const fieldKeys = inspectionFields.map((f) => f.key);
                        const mid = Math.ceil(fieldKeys.length / 2);
                        const dynamicColumns = [fieldKeys.slice(0, mid), fieldKeys.slice(mid)];

                        return (
                          <Accordion key={reviewActionValue} sx={{ mx: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle1">
                                {reviewAction.displayName}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Stack spacing={3} sx={{ m: 2 }}>
                                {mode === 'Edit' && (
                                  <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Autocomplete
                                      sx={{ flex: 1 }}
                                      size="small"
                                      options={getAvailableComponentInspectionFields(
                                        reviewActionValue
                                      )}
                                      getOptionLabel={(option) => option.label}
                                      value={selectedFieldForInspection[reviewActionValue] ?? null}
                                      onChange={(_event, newValue) => {
                                        setSelectedFieldForInspection((prev) => ({
                                          ...prev,
                                          [reviewActionValue]: newValue,
                                        }));
                                      }}
                                      groupBy={(option) =>
                                        option.source === 'template'
                                          ? 'Template Fields'
                                          : 'Schema Fields'
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Search and select field to add"
                                          placeholder="Type to search..."
                                          helperText={`${inspectionFields.length} field(s) added (max 5 visible). First 5 are checked; additional columns are unselected.`}
                                        />
                                      )}
                                      renderOption={(props, option) => {
                                        const isAlreadyAdded = inspectionFields.some(
                                          (field) => field.key === option.key
                                        );
                                        return (
                                          <li
                                            {...props}
                                            style={{
                                              ...props.style,
                                              backgroundColor: isAlreadyAdded
                                                ? abyssColors.success[50]
                                                : undefined,
                                            }}
                                          >
                                            <Stack
                                              direction="row"
                                              spacing={1}
                                              alignItems="center"
                                              sx={{ width: '100%' }}
                                            >
                                              {isAlreadyAdded && (
                                                <CheckCircle
                                                  fontSize="small"
                                                  sx={{ color: abyssColors.success[500] }}
                                                />
                                              )}
                                              <Stack sx={{ flex: 1 }}>
                                                <Stack
                                                  direction="row"
                                                  spacing={1}
                                                  alignItems="center"
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight: isAlreadyAdded ? 600 : 400,
                                                      color: isAlreadyAdded
                                                        ? abyssColors.success[700]
                                                        : 'inherit',
                                                    }}
                                                  >
                                                    {option.label}
                                                  </Typography>
                                                  {isAlreadyAdded && (
                                                    <Chip
                                                      label="Added"
                                                      size="small"
                                                      sx={{
                                                        height: '18px',
                                                        fontSize: '0.65rem',
                                                        backgroundColor: abyssColors.success[100],
                                                        color: abyssColors.success[700],
                                                      }}
                                                    />
                                                  )}
                                                </Stack>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ fontFamily: 'monospace' }}
                                                >
                                                  {option.key}
                                                </Typography>
                                              </Stack>
                                              {option.source === 'template' && (
                                                <Chip
                                                  label="Template"
                                                  size="small"
                                                  sx={{
                                                    height: '20px',
                                                    fontSize: '0.7rem',
                                                    backgroundColor: abyssColors.primary[100],
                                                    color: abyssColors.primary[700],
                                                  }}
                                                />
                                              )}
                                            </Stack>
                                          </li>
                                        );
                                      }}
                                      getOptionDisabled={(option) => {
                                        // Disable already added fields
                                        return inspectionFields.some(
                                          (field) => field.key === option.key
                                        );
                                      }}
                                    />
                                    <Button
                                      variant="contained"
                                      onClick={() =>
                                        handleAddComponentInspectionField(reviewActionValue)
                                      }
                                      disabled={!selectedFieldForInspection[reviewActionValue]}
                                      sx={containedButtonStyle}
                                    >
                                      Add Field
                                    </Button>
                                  </Stack>
                                )}
                                {inspectionFields.length > 0 ? (
                                  <ReportConfigList
                                    mode={mode}
                                    reportConfig={inspectionFields as ReportOperationalFields[]}
                                    columns={dynamicColumns}
                                    enableEditing={false}
                                    onToggleKey={(key: string, checked: boolean) =>
                                      handleComponentInspectionTableToggle(
                                        reviewActionValue,
                                        key,
                                        checked
                                      )
                                    }
                                    onUpdateTitle={handleTitleChange}
                                    maxCheckedFields={5}
                                    maxCheckedTooltip="Only 5 fields are allowed"
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                    No fields configured. Use the "Add Field" option above to add
                                    fields to this review action.
                                  </Typography>
                                )}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Stack>
                    <Box sx={{ paddingTop: '3rem' }}></Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      POI Report Images
                    </Typography>
                    <DropDownContainer>
                      <Typography>POI Images</Typography>
                      <DropDownField
                        mode={mode}
                        id="poi-report-poi-orientation"
                        title=""
                        sx={dropDownStyles}
                        disableClearable
                        disabled={mode !== 'Edit'}
                        value={formatEnumForDisplay(
                          getOrientationValue(
                            localFields.reportConfig
                              ?.imageOrientationConfig as ImageOrientationConfigState,
                            'poiReportPoiImages'
                          )
                        )}
                        options={Object.values(ImageOrientation).map((value) =>
                          formatEnumForDisplay(value)
                        )}
                        onFieldValueChange={(value) => {
                          setLocalFields((prev) => {
                            if (!prev || !prev.reportConfig) return prev;
                            return {
                              ...prev,
                              reportConfig: {
                                ...prev.reportConfig,
                                imageOrientationConfig: {
                                  ...(prev.reportConfig.imageOrientationConfig ?? {}),
                                  poiReportPoiImages: upperSnakeCase(value) as ImageOrientation,
                                },
                              },
                            };
                          });
                        }}
                      />
                    </DropDownContainer>
                    <DropDownContainer>
                      <Typography>Deck Plan Images</Typography>
                      <DropDownField
                        mode={mode}
                        id="poi-report-deck-orientation"
                        title=""
                        sx={dropDownStyles}
                        disableClearable
                        disabled={mode !== 'Edit'}
                        value={formatEnumForDisplay(
                          getOrientationValue(
                            localFields.reportConfig
                              ?.imageOrientationConfig as ImageOrientationConfigState,
                            'poiReportDeckImages'
                          )
                        )}
                        options={Object.values(ImageOrientation).map((value) =>
                          formatEnumForDisplay(value)
                        )}
                        onFieldValueChange={(value) => {
                          setLocalFields((prev) => {
                            if (!prev || !prev.reportConfig) return prev;
                            return {
                              ...prev,
                              reportConfig: {
                                ...prev.reportConfig,
                                imageOrientationConfig: {
                                  ...(prev.reportConfig.imageOrientationConfig ?? {}),
                                  poiReportDeckImages: upperSnakeCase(value) as ImageOrientation,
                                },
                              },
                            };
                          });
                        }}
                      />
                    </DropDownContainer>
                    <Box sx={{ paddingTop: '2rem' }}></Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Component Report Images
                    </Typography>
                    <DropDownContainer>
                      <Typography>POI Images</Typography>
                      <DropDownField
                        mode={mode}
                        id="component-report-poi-orientation"
                        title=""
                        sx={dropDownStyles}
                        disableClearable
                        disabled={mode !== 'Edit'}
                        value={formatEnumForDisplay(
                          getOrientationValue(
                            localFields.reportConfig
                              ?.imageOrientationConfig as ImageOrientationConfigState,
                            'componentReportPoiImages'
                          )
                        )}
                        options={Object.values(ImageOrientation).map((value) =>
                          formatEnumForDisplay(value)
                        )}
                        onFieldValueChange={(value) => {
                          setLocalFields((prev) => {
                            if (!prev || !prev.reportConfig) return prev;
                            return {
                              ...prev,
                              reportConfig: {
                                ...prev.reportConfig,
                                imageOrientationConfig: {
                                  ...(prev.reportConfig.imageOrientationConfig ?? {}),
                                  componentReportPoiImages: upperSnakeCase(
                                    value
                                  ) as ImageOrientation,
                                },
                              },
                            };
                          });
                        }}
                      />
                    </DropDownContainer>
                    <DropDownContainer>
                      <Typography>Deck Plan Images</Typography>
                      <DropDownField
                        mode={mode}
                        id="component-report-deck-orientation"
                        title=""
                        sx={dropDownStyles}
                        disableClearable
                        disabled={mode !== 'Edit'}
                        value={formatEnumForDisplay(
                          getOrientationValue(
                            localFields.reportConfig
                              ?.imageOrientationConfig as ImageOrientationConfigState,
                            'componentReportDeckImages'
                          )
                        )}
                        options={Object.values(ImageOrientation).map((value) =>
                          formatEnumForDisplay(value)
                        )}
                        onFieldValueChange={(value) => {
                          setLocalFields((prev) => {
                            if (!prev || !prev.reportConfig) return prev;
                            return {
                              ...prev,
                              reportConfig: {
                                ...prev.reportConfig,
                                imageOrientationConfig: {
                                  ...(prev.reportConfig.imageOrientationConfig ?? {}),
                                  componentReportDeckImages: upperSnakeCase(
                                    value
                                  ) as ImageOrientation,
                                },
                              },
                            };
                          });
                        }}
                      />
                    </DropDownContainer>
                    <Autocomplete
                      multiple
                      sx={{ width: '100%' }}
                      id="poi-template-types-autocomplete"
                      options={allPointOfInterestTemplates ?? []}
                      getOptionLabel={(option) => option.name}
                      value={
                        allPointOfInterestTemplates?.filter((template) =>
                          localFields.reportConfig?.paintBlockReportPoiTemplateTypes?.includes(
                            template?.key?.toUpperCase() || ''
                          )
                        ) ?? []
                      }
                      onChange={(_event, value) => {
                        setLocalFields((prev) => {
                          if (!prev || !prev.reportConfig) return prev;
                          return {
                            ...prev,
                            reportConfig: {
                              ...prev.reportConfig,
                              paintBlockReportPoiTemplateTypes: value
                                .map((template) => template.key?.toUpperCase())
                                .filter((key): key is string => key !== null && key !== undefined),
                            },
                          };
                        });
                      }}
                      disabled={mode !== 'Edit'}
                      autoComplete
                      includeInputInList
                      renderInput={(parameters) => (
                        <TextField
                          {...parameters}
                          label="Paint Block Report POI Types"
                          variant="outlined"
                        />
                      )}
                      slotProps={{
                        chip: {
                          sx: {
                            backgroundColor: abyssColors.primary[100],
                            color: abyssColors.primary[700],
                            '& .MuiChip-deleteIcon': {
                              color: abyssColors.primary[500],
                              '&:hover': {
                                color: abyssColors.primary[700],
                              },
                            },
                          },
                        },
                      }}
                      noOptionsText="No POI templates found"
                    />
                  </Stack>
                ) : (
                  <FieldPanel gap={3} direction="row">
                    <Stack width="25%" spacing={1} sx={{ pt: 2, pl: 2 }} alignItems="flex-start">
                      <Button
                        startIcon={<Add />}
                        variant="contained"
                        onClick={() => setIsConfirmationDialogOpen(true)}
                        sx={containedButtonStyle}
                      >
                        Add Default Report Configuration
                      </Button>
                    </Stack>
                  </FieldPanel>
                )}
              </>
            )}

            {selectedField === StructureSection.SummaryChartsConfig && (
              <FieldPanel gap={3} direction="row">
                {localFields?.reportConfig ? (
                  <Box sx={{ width: '100%' }}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        POI Summary charts
                      </Typography>
                      <Tooltip
                        title="Controls which summary widgets appear in Insights → Points of Interest for each POI type tab and the All tab."
                        placement="top"
                        arrow
                        slotProps={{
                          popper: { sx: tooltipStyles },
                        }}
                      >
                        <InfoIcon fontSize="small" sx={{ color: abyssColors.primary[400] }} />
                      </Tooltip>
                    </Stack>

                    {(() => {
                      const config = (
                        localFields.reportConfig as unknown as { poiSummaryChartsConfig?: unknown }
                      )?.poiSummaryChartsConfig as
                        | Record<string, POIWidgets[] | undefined>
                        | undefined;

                      const items: Array<{ label: string; key: string }> = [
                        { label: 'All', key: 'ALL' },
                        ...(allPointOfInterestTemplates ?? []).map((t) => ({
                          label: t.name ?? t.key ?? '',
                          key: upperSnakeCase((t.key ?? t.name ?? '').toString()),
                        })),
                      ].filter((i) => i.label && i.key);

                      const sameSet = (a: POIWidgets[], b: POIWidgets[]) => {
                        if (a.length !== b.length) return false;
                        const s = new Set(a);
                        return b.every((x) => s.has(x));
                      };

                      const toggleWidget = (key: string, widget: POIWidgets) => {
                        if (mode !== 'Edit') return;
                        setLocalFields((prev) => {
                          if (!prev?.reportConfig) return prev;
                          const prevCfg = (
                            prev.reportConfig as unknown as { poiSummaryChartsConfig?: unknown }
                          )?.poiSummaryChartsConfig as
                            | Record<string, POIWidgets[] | undefined>
                            | undefined;
                          const current = (prevCfg?.[key] ??
                            defaultPoiSummaryChartsConfig[key] ??
                            []) as POIWidgets[];
                          const next = current.includes(widget)
                            ? current.filter((w) => w !== widget)
                            : [...current, widget];
                          return {
                            ...prev,
                            reportConfig: {
                              ...prev.reportConfig,
                              poiSummaryChartsConfig: {
                                ...(prevCfg ?? {}),
                                [key]: next,
                              },
                            } as unknown as ReportConfig,
                          };
                        });
                      };

                      return (
                        <Stack gap={1}>
                          {items.map(({ label, key }) => {
                            const selected = (config?.[key] ??
                              defaultPoiSummaryChartsConfig[key] ??
                              []) as POIWidgets[];
                            const defaultForKey = (defaultPoiSummaryChartsConfig[key] ??
                              []) as POIWidgets[];
                            const isCustomized = !sameSet(selected, defaultForKey);

                            return (
                              <Accordion key={key} disableGutters elevation={0}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Stack direction="row" alignItems="center" gap={1} width="100%">
                                    <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                                    {isCustomized && (
                                      <Stack direction="row" alignItems="center" gap={0.5}>
                                        <CheckCircle
                                          fontSize="small"
                                          sx={{ color: abyssColors.primary[500] }}
                                        />
                                        <Typography
                                          variant="caption"
                                          sx={{ color: abyssColors.primary[500] }}
                                        >
                                          Configured
                                        </Typography>
                                      </Stack>
                                    )}
                                  </Stack>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <FormGroup>
                                    {Object.values(POIWidgets).map((widget) => (
                                      <FormControlLabel
                                        key={`${key}-${widget}`}
                                        control={
                                          <Checkbox
                                            checked={selected.includes(widget)}
                                            onChange={() => toggleWidget(key, widget)}
                                            disabled={mode !== 'Edit'}
                                          />
                                        }
                                        label={widget}
                                      />
                                    ))}
                                  </FormGroup>
                                </AccordionDetails>
                              </Accordion>
                            );
                          })}
                        </Stack>
                      );
                    })()}
                  </Box>
                ) : (
                  <Stack width="25%" spacing={1} sx={{ pt: 2, pl: 2 }} alignItems="flex-start">
                    <Button
                      startIcon={<Add />}
                      variant="contained"
                      onClick={() => setIsConfirmationDialogOpen(true)}
                      sx={containedButtonStyle}
                    >
                      Add Default Report Configuration
                    </Button>
                  </Stack>
                )}
              </FieldPanel>
            )}

            {selectedField === StructureSection.NominationPoiTypes && (
              <FieldPanel gap={3} direction="row">
                <Box sx={{ width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    POI Nomination Types
                  </Typography>

                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    sx={{ width: '100%' }}
                    id="nomination-poi-template-types-autocomplete"
                    options={(() => {
                      return (allPointOfInterestTemplates ?? []).filter((template) =>
                        ALLOWED_NOMINATION_POI_TEMPLATE.includes(
                          (template?.key ?? '').toUpperCase()
                        )
                      );
                    })()}
                    getOptionLabel={(option) => (option.key ?? option.name ?? '').toUpperCase()}
                    value={
                      (allPointOfInterestTemplates ?? []).filter((template) =>
                        (localFields?.nominationConfig?.poiTemplateNominationTypes ?? []).includes(
                          (template?.key ?? '').toUpperCase()
                        )
                      ) ?? []
                    }
                    onChange={(_event, value) => {
                      setLocalFields((prev) => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          nominationConfig: {
                            ...(prev.nominationConfig ?? {
                              poiTemplateNominationTypes: [],
                              nominationTableConfig: {
                                title: 'Allowed POIs for Nomination',
                                columns: [],
                              },
                            }),
                            poiTemplateNominationTypes: value
                              .map((template) => template.key?.toUpperCase())
                              .filter((key): key is string => key !== null && key !== undefined),
                          },
                        };
                      });
                    }}
                    disabled={mode !== 'Edit'}
                    autoComplete
                    includeInputInList
                    renderInput={(parameters) => (
                      <TextField {...parameters} label="Nomination POI Types" variant="outlined" />
                    )}
                    slotProps={{
                      chip: {
                        sx: {
                          backgroundColor: abyssColors.primary[100],
                          color: abyssColors.primary[700],
                          '& .MuiChip-deleteIcon': {
                            color: abyssColors.primary[500],
                            '&:hover': {
                              color: abyssColors.primary[700],
                            },
                          },
                        },
                      },
                    }}
                    noOptionsText="No POI templates found"
                  />

                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="POI Table Title"
                      value={localFields?.nominationConfig?.nominationTableConfig?.title ?? ''}
                      onChange={(e) => {
                        if (mode !== 'Edit') return;
                        const newTitle = e.target.value;
                        setLocalFields((prev) => {
                          if (!prev) return prev;
                          return {
                            ...prev,
                            nominationConfig: {
                              ...(prev.nominationConfig ?? {
                                poiTemplateNominationTypes: [],
                                nominationTableConfig: {
                                  title: 'Allowed POIs for Nomination',
                                  columns: [],
                                },
                              }),
                              nominationTableConfig: {
                                ...(prev.nominationConfig?.nominationTableConfig ?? {
                                  title: 'Allowed POIs for Nomination',
                                  columns: [],
                                }),
                                title: newTitle,
                              },
                            },
                          };
                        });
                      }}
                      disabled={mode === 'ReadOnly'}
                      fullWidth
                      size="small"
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      POI Table Headers
                    </Typography>
                    {(localFields?.nominationConfig?.nominationTableConfig?.columns ?? []).map(
                      (field, idx) => (
                        <Stack
                          key={`nom-field-${idx}`}
                          spacing={1}
                          sx={{
                            border: `1px solid ${abyssColors.neutral[300]}`,
                            borderRadius: 1,
                            p: 2,
                            mb: 1,
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                              size="small"
                              label="Key"
                              value={field?.key ?? ''}
                              disabled
                              sx={{ width: 320 }}
                            />

                            <TextField
                              size="small"
                              label="Display Name"
                              value={field?.displayName ?? ''}
                              onChange={(e) => {
                                if (mode !== 'Edit') return;
                                const newVal = e.target.value;
                                setLocalFields((prev) => {
                                  if (!prev) return prev;
                                  const fields = (
                                    prev.nominationConfig?.nominationTableConfig?.columns ?? []
                                  ).slice();
                                  fields[idx] = { ...(fields[idx] ?? {}), displayName: newVal };
                                  return {
                                    ...prev,
                                    nominationConfig: {
                                      ...(prev.nominationConfig ?? {
                                        poiTemplateNominationTypes: [],
                                        nominationTableConfig: {
                                          title: 'Allowed POIs for Nomination',
                                          columns: [],
                                        },
                                      }),
                                      nominationTableConfig: {
                                        ...(prev.nominationConfig?.nominationTableConfig ?? {
                                          title: 'Allowed POIs for Nomination',
                                          columns: [],
                                        }),
                                        columns: fields,
                                      },
                                    },
                                  };
                                });
                              }}
                              disabled={mode === 'ReadOnly'}
                              sx={{ width: 360 }}
                            />

                            <IconButton
                              size="small"
                              onClick={() => {
                                setLocalFields((prev) => {
                                  if (!prev) return prev;
                                  const fields = (
                                    prev.nominationConfig?.nominationTableConfig?.columns ?? []
                                  ).slice();
                                  fields.splice(idx, 1);
                                  return {
                                    ...prev,
                                    nominationConfig: {
                                      ...(prev.nominationConfig ?? {
                                        poiTemplateNominationTypes: [],
                                        nominationTableConfig: {
                                          title: 'Allowed POIs for Nomination',
                                          columns: [],
                                        },
                                      }),
                                      nominationTableConfig: {
                                        ...(prev.nominationConfig?.nominationTableConfig ?? {
                                          title: 'Allowed POIs for Nomination',
                                          columns: [],
                                        }),
                                        columns: fields,
                                      },
                                    },
                                  };
                                });
                              }}
                              disabled={mode === 'ReadOnly'}
                              sx={{ ml: 'auto' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      )
                    )}

                    {mode === 'Edit' && (
                      <NominationFieldsEditor
                        mode={mode}
                        allowedTemplateKeys={ALLOWED_NOMINATION_POI_TEMPLATE}
                        existingKeys={(
                          localFields?.nominationConfig?.nominationTableConfig?.columns ?? []
                        ).map(
                          (column: NominationTableColumn | null | undefined) => column?.key ?? ''
                        )}
                        onAddColumn={(newField) => {
                          setLocalFields((prev) => {
                            if (!prev) return prev;
                            const fields = prev.nominationConfig?.nominationTableConfig?.columns
                              ? prev.nominationConfig.nominationTableConfig.columns.slice()
                              : [];
                            if (!fields.some((f) => f?.key === newField.key)) fields.push(newField);
                            return {
                              ...prev,
                              nominationConfig: {
                                ...(prev.nominationConfig ?? {
                                  poiTemplateNominationTypes: [],
                                  nominationTableConfig: {
                                    title: 'Allowed POIs for Nomination',
                                    columns: [],
                                  },
                                }),
                                nominationTableConfig: {
                                  ...(prev.nominationConfig?.nominationTableConfig ?? {
                                    title: 'Allowed POIs for Nomination',
                                    columns: [],
                                  }),
                                  columns: fields,
                                },
                              },
                            };
                          });
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </FieldPanel>
            )}

            {selectedField === StructureSection.StructurePipes && structurePipes.length > 0 ? (
              <Box width="40%">
                <ConfigurationTable
                  items={structurePipes}
                  columns={columns}
                  editingId={editingId}
                  emptyMessage="No pipe sizes"
                  onEditStart={handleEditStart}
                  onEditCancel={handleEditCancel}
                  onEditSave={handleEditStructurePipe}
                  onInlineChange={handleInlineChange}
                  onAddNew={handleAddStructurePipe}
                  onDelete={handleDeleteStructurePipe}
                />
              </Box>
            ) : (
              selectedField === StructureSection.StructurePipes && (
                <Box sx={{ paddingLeft: '40rem' }}>
                  <FeedbackCircle
                    diameter={600}
                    text="No structure pipes found for the selected category"
                  ></FeedbackCircle>
                </Box>
              )
            )}
          </>
        ) : (
          <FieldPanel justifyContent="center" alignItems="center">
            <EmptyStatePlaceholder
              type="template"
              message="Click on the structure in the left panel to see its details"
            />
          </FieldPanel>
        )}
      </Stack>

      {isOpen && <ImageUploadDialog open={isOpen} onClose={handleDialogClose} />}
      {isConfirmationDialogOpen && (
        <GenericDialog
          open={isConfirmationDialogOpen}
          onClose={() => setIsConfirmationDialogOpen(false)}
          title="Add Report Config"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to add the default report configuration?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsConfirmationDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAddReportConfig()}
                sx={containedButtonStyle}
                disabled={isLoading}
              >
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {isFleetHealthModalOpen && (
        <FleetHealthTemplateModal
          open={isFleetHealthModalOpen}
          fieldName={fleetHealthPreview.fieldName}
          options={fleetHealthPreview.options}
          totalCount={fleetHealthPreview.totalCount}
          hasOptions={fleetHealthPreview.hasOptions}
          onConfirm={handleFleetHealthConfirm}
          onCancel={handleFleetHealthCancel}
          onClose={() => setIsFleetHealthModalOpen(false)}
        />
      )}
    </Stack>
  );
};
